import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store';
import { useSearchEntities } from '../../api/entities';
import { useGlobalSearch } from '../../api/intelligence';
import { useTranslation } from '../../i18n';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { useNotificationStore } from '../../store/notifications';
import type { Entity } from '../../types';

export default function TopBar() {
  const { searchQuery, setSearchQuery, theme, toggleTheme, notifications, markAllNotificationsRead } =
    useAppStore();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // New notification store for the slide-over panel
  const notifStoreUnread = useNotificationStore((s) => s.unreadCount);
  const toggleNotifPanel = useNotificationStore((s) => s.togglePanel);

  const [showSearch, setShowSearch] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: searchResults } = useSearchEntities(localQuery);
  const { data: globalResults } = useGlobalSearch(localQuery);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSearchSelect(entity: Entity) {
    setSearchQuery(entity.name);
    setShowSearch(false);
    navigate(`/entities/${entity.id}`);
  }

  function handleGlobalResultClick(result: { id: string; module: string; url: string | null }) {
    setShowSearch(false);
    setLocalQuery('');
    if (result.url) {
      navigate(result.url);
    } else if (result.module === 'entities') {
      navigate(`/entities/${result.id}`);
    }
  }

  // Combine unread counts from both stores
  const totalUnread = notifications.unreadCount + notifStoreUnread;

  return (
    <header className="h-16 flex items-center justify-between px-6 border-b border-border-subtle bg-surface-secondary/60 backdrop-blur-md">
      {/* Search */}
      <div ref={searchRef} className="relative flex-1 max-w-xl">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            aria-label="Recherche globale"
            placeholder={t('search.placeholder')}
            value={localQuery}
            onChange={(e) => {
              setLocalQuery(e.target.value);
              setShowSearch(true);
            }}
            onFocus={() => setShowSearch(true)}
            className="w-full pl-10 pr-4 py-2 bg-surface-tertiary border border-border-subtle rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/40 focus:ring-1 focus:ring-cyber-cyan/20 transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[10px] font-mono text-gray-500 bg-surface-primary rounded border border-border-subtle">
            Ctrl+K
          </kbd>
        </div>

        {/* Search results dropdown */}
        {showSearch && localQuery.length >= 2 && (
          <div className="absolute top-full mt-2 w-full bg-surface-secondary border border-border-subtle rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto">
            {/* Entity results */}
            {searchResults && searchResults.length > 0 && (
              <>
                <div className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b border-border-subtle">
                  Entites
                </div>
                {searchResults.slice(0, 5).map((entity) => (
                  <button
                    key={entity.id}
                    onClick={() => handleSearchSelect(entity)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-tertiary text-left transition-colors"
                  >
                    <EntityTypeIcon type={entity.type} />
                    <div>
                      <p className="text-sm font-medium text-gray-200">{entity.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{entity.type.replace('_', ' ')}</p>
                    </div>
                    <span className="ml-auto text-xs text-gray-600">
                      {t('search.risk')}: {Math.round(entity.risk_score * 100)}%
                    </span>
                  </button>
                ))}
              </>
            )}
            {/* Cross-module results */}
            {globalResults && globalResults.results && globalResults.results.filter(r => r.module !== 'entities').length > 0 && (
              <>
                <div className="px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b border-border-subtle">
                  Autres modules ({globalResults.search_time_ms.toFixed(0)}ms)
                </div>
                {globalResults.results.filter(r => r.module !== 'entities').slice(0, 8).map((result) => (
                  <button
                    key={`${result.module}-${result.id}`}
                    onClick={() => handleGlobalResultClick(result)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-tertiary text-left transition-colors"
                  >
                    <ModuleIcon module={result.module} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-200 truncate">{result.title}</p>
                      <p className="text-xs text-gray-500 truncate">{result.snippet}</p>
                    </div>
                    <span className="ml-auto text-[10px] text-gray-600 uppercase">{result.module}</span>
                  </button>
                ))}
              </>
            )}
            {(!searchResults || searchResults.length === 0) && (!globalResults || !globalResults.results || globalResults.results.length === 0) && (
              <div className="px-4 py-6 text-center text-sm text-gray-500">Aucun resultat</div>
            )}
          </div>
        )}
      </div>

      {/* Right section */}
      <div className="flex items-center gap-3 ml-4">
        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          aria-label={`Basculer en mode ${theme === 'dark' ? 'clair' : 'sombre'}`}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary transition-colors"
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            </svg>
          )}
        </button>

        {/* Notifications — bell opens the NotificationPanel slide-over */}
        <div className="relative">
          <button
            onClick={() => {
              toggleNotifPanel();
              // Also mark legacy store notifications as read when opening
              markAllNotificationsRead();
            }}
            aria-label={`Notifications${totalUnread > 0 ? ` (${totalUnread} non lues)` : ''}`}
            className="relative p-2 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            {totalUnread > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-cyber-red text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {totalUnread > 9 ? '9+' : totalUnread}
              </span>
            )}
          </button>
        </div>

        {/* User */}
        <div className="flex items-center gap-2 pl-3 border-l border-border-subtle">
          <div className="w-8 h-8 rounded-full bg-helios-700 flex items-center justify-center text-sm font-medium text-cyber-cyan">
            A
          </div>
          <span className="text-sm text-gray-300 hidden sm:block">Analyst</span>
        </div>
      </div>
    </header>
  );
}

function EntityTypeIcon({ type }: { type: string }) {
  const colors: Record<string, string> = {
    person: 'bg-blue-500/20 text-blue-400',
    organization: 'bg-purple-500/20 text-purple-400',
    location: 'bg-green-500/20 text-green-400',
    ip_address: 'bg-red-500/20 text-red-400',
    domain: 'bg-orange-500/20 text-orange-400',
    email: 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${colors[type] || 'bg-gray-500/20 text-gray-400'}`}>
      {type.charAt(0).toUpperCase()}
    </div>
  );
}

function ModuleIcon({ module }: { module: string }) {
  const styles: Record<string, string> = {
    darkweb: 'bg-red-500/20 text-red-400',
    alerts: 'bg-yellow-500/20 text-yellow-400',
    osint: 'bg-emerald-500/20 text-emerald-400',
    relationships: 'bg-indigo-500/20 text-indigo-400',
    agents: 'bg-violet-500/20 text-violet-400',
  };
  const labels: Record<string, string> = {
    darkweb: 'DW', alerts: 'AL', osint: 'OS', relationships: 'RE', agents: 'AG',
  };
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold ${styles[module] || 'bg-gray-500/20 text-gray-400'}`}>
      {labels[module] || module.slice(0, 2).toUpperCase()}
    </div>
  );
}
